<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useRoute, useRouter } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

// The order the filter chips are drawn in, most common brain first. The keys are spelled out
// rather than built from the brain name so the i18n linter can still find them.
const BRAINS = [
  {
    id: 'ai_agent',
    labelKey: 'AGENT_BOTS.TYPES.AI_AGENT',
    filterKey: 'AGENT_BOTS.FILTERS.AI_AGENT',
  },
  {
    id: 'flow',
    labelKey: 'AGENT_BOTS.TYPES.FLOW',
    filterKey: 'AGENT_BOTS.FILTERS.FLOW',
  },
  {
    id: 'webhook',
    labelKey: 'AGENT_BOTS.TYPES.WEBHOOK',
    filterKey: 'AGENT_BOTS.FILTERS.WEBHOOK',
  },
  {
    id: 'remote',
    labelKey: 'AGENT_BOTS.TYPES.REMOTE',
    filterKey: 'AGENT_BOTS.FILTERS.REMOTE',
  },
];

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const selectedBot = ref({});
const searchQuery = ref('');
const brainFilter = ref('all');
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);

const tableHeaders = computed(() => [
  t('AGENT_BOTS.LIST.TABLE_HEADER.DETAILS'),
  t('AGENT_BOTS.LIST.TABLE_HEADER.BRAIN'),
  t('AGENT_BOTS.LIST.TABLE_HEADER.CHANNEL'),
  t('AGENT_BOTS.LIST.TABLE_HEADER.STATUS'),
  t('AGENT_BOTS.LIST.TABLE_HEADER.ACTIONS'),
]);

const selectedBotName = computed(() => selectedBot.value?.name || '');

const filteredAgentBots = computed(() => {
  const brain = brainFilter.value;
  const byBrain =
    brain === 'all'
      ? agentBots.value
      : agentBots.value.filter(bot => bot.bot_type === brain);

  const query = searchQuery.value.trim();
  if (!query) return byBrain;
  return picoSearch(byBrain, query, ['name', 'description']);
});

const filters = computed(() => [
  {
    id: 'all',
    label: t('AGENT_BOTS.FILTERS.ALL'),
    count: agentBots.value.length,
  },
  ...BRAINS.map(brain => ({
    id: brain.id,
    label: t(brain.filterKey),
    count: agentBots.value.filter(bot => bot.bot_type === brain.id).length,
  })).filter(filter => filter.count > 0),
]);

// A bot linked to no inbox is never called, whatever else is configured on it.
const orphanCount = computed(
  () => agentBots.value.filter(bot => !bot.inboxes?.length).length
);

const brainLabel = bot => {
  const brain = BRAINS.find(item => item.id === bot.bot_type);
  return t(brain ? brain.labelKey : 'AGENT_BOTS.TYPES.WEBHOOK');
};

// What the brain actually is: the flow it runs, the AI agent behind it, or the host it calls.
const brainDetail = bot => {
  if (bot.bot_type === 'flow') return bot.flow_name || '';
  if (bot.bot_type === 'ai_agent') return bot.assistant_name || '';

  const url = bot.outgoing_url || bot.bot_config?.webhook_url;
  return url || '';
};

const channelLabel = bot =>
  bot.inboxes?.length
    ? bot.inboxes.map(inbox => inbox.name).join(', ')
    : t('AGENT_BOTS.NO_CHANNEL');

const openAddModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedBot.value = {};
  agentBotModalRef.value.dialogRef.open();
};

// An AI agent opens on its own page (instructions, knowledge, inboxes); the other bots on theirs.
const agentRoute = bot => ({
  name: 'captain_assistants_settings_index',
  params: {
    accountId: route.params.accountId,
    assistantId: bot.bot_config.assistant_id,
  },
});
const isAgent = bot =>
  bot.bot_type === 'ai_agent' && Boolean(bot.bot_config?.assistant_id);

const openBot = bot => {
  if (isAgent(bot)) {
    router.push(agentRoute(bot));
    return;
  }
  router.push(
    frontendURL(
      `accounts/${route.params.accountId}/settings/agent-bots/${bot.id}`
    )
  );
};

const openEditModal = bot => {
  if (isAgent(bot)) {
    router.push(agentRoute(bot));
    return;
  }
  modalType.value = MODAL_TYPES.EDIT;
  selectedBot.value = bot;
  agentBotModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const toggleActive = async bot => {
  loading.value[bot.id] = true;
  try {
    await store.dispatch('agentBots/update', {
      id: bot.id,
      data: { active: !bot.active },
    });
    useAlert(
      bot.active
        ? t('AGENT_BOTS.TOGGLE.DISABLED')
        : t('AGENT_BOTS.TOGGLE.ENABLED')
    );
  } catch (error) {
    useAlert(t('AGENT_BOTS.TOGGLE.ERROR'));
  } finally {
    loading.value[bot.id] = false;
  }
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

const confirmDeletion = () => {
  loading.value[selectedBot.value.id] = true;
  deleteAgentBot(selectedBot.value.id);
  agentBotDeleteDialogRef.value.close();
};

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
    :no-records-found="!agentBots.length"
    :no-records-message="t('AGENT_BOTS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('AGENT_BOTS.HEADER')"
        :description="t('AGENT_BOTS.DESCRIPTION')"
        :link-text="t('AGENT_BOTS.LEARN_MORE')"
        :search-placeholder="t('AGENT_BOTS.SEARCH_PLACEHOLDER')"
        feature-name="agent_bots"
      >
        <template v-if="agentBots?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_BOTS.COUNT', { n: agentBots.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_BOTS.ADD.TITLE')"
            size="sm"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <Banner v-if="orphanCount" color="amber" class="mb-4">
        {{ $t('AGENT_BOTS.ORPHANS', orphanCount, { count: orphanCount }) }}
      </Banner>

      <div v-if="filters.length > 2" class="flex gap-2 mb-4 flex-wrap">
        <Button
          v-for="filter in filters"
          :key="filter.id"
          size="sm"
          :variant="brainFilter === filter.id ? 'solid' : 'faded'"
          :color="brainFilter === filter.id ? 'blue' : 'slate'"
          :label="`${filter.label} (${filter.count})`"
          @click="brainFilter = filter.id"
        />
      </div>

      <BaseTable
        :headers="tableHeaders"
        :items="filteredAgentBots"
        :no-data-message="
          searchQuery ? t('AGENT_BOTS.NO_RESULTS') : t('AGENT_BOTS.LIST.404')
        "
      >
        <template #row="{ items }">
          <BaseTableRow v-for="bot in items" :key="bot.id" :item="bot">
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex items-center gap-4 min-w-0">
                  <Avatar
                    :name="bot.name || ''"
                    :src="bot.thumbnail"
                    :size="40"
                    class="flex-shrink-0"
                  />
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        class="truncate text-body-main text-n-slate-12 hover:text-n-brand"
                        @click="openBot(bot)"
                      >
                        {{ bot.name }}
                      </button>
                      <span
                        v-if="bot.system_bot"
                        class="text-xs text-n-slate-12 bg-n-blue-5 rounded-md py-0.5 px-1 flex-shrink-0"
                      >
                        {{ $t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}
                      </span>
                    </div>
                    <span class="text-body-main text-n-slate-11 block truncate">
                      {{ bot.description }}
                    </span>
                  </div>
                </div>
              </BaseTableCell>

              <BaseTableCell class="w-44 max-w-44">
                <span
                  class="text-xs text-n-slate-12 bg-n-slate-3 rounded-md py-0.5 px-1.5 inline-block whitespace-nowrap"
                >
                  {{ brainLabel(bot) }}
                </span>
                <span
                  v-if="brainDetail(bot)"
                  class="text-body-main text-n-slate-11 block truncate"
                >
                  {{ brainDetail(bot) }}
                </span>
              </BaseTableCell>

              <BaseTableCell class="w-56 max-w-56">
                <span
                  class="text-body-main truncate block"
                  :class="
                    bot.inboxes?.length ? 'text-n-slate-11' : 'text-n-amber-11'
                  "
                >
                  {{ channelLabel(bot) }}
                </span>
              </BaseTableCell>

              <BaseTableCell class="w-32">
                <div v-if="!bot.system_bot" class="flex items-center gap-2">
                  <Switch
                    :model-value="bot.active"
                    @change="toggleActive(bot)"
                  />
                  <span class="text-body-main text-n-slate-11">
                    {{
                      bot.active
                        ? $t('AGENT_BOTS.STATUS.ACTIVE')
                        : $t('AGENT_BOTS.STATUS.INACTIVE')
                    }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-if="!bot.system_bot"
                    v-tooltip.top="t('AGENT_BOTS.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[bot.id]"
                    @click="openEditModal(bot)"
                  />
                  <Button
                    v-if="!bot.system_bot"
                    v-tooltip.top="t('AGENT_BOTS.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[bot.id]"
                    @click="openDeletePopup(bot)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <AgentBotModal
      ref="agentBotModalRef"
      :type="modalType"
      :selected-bot="selectedBot"
    />

    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      :title="t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        selectedBot.bot_type === 'ai_agent'
          ? t('AGENT_BOTS.DELETE.CONFIRM.AGENT_MESSAGE', {
              name: selectedBotName,
            })
          : t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
